import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { NullSafe } from 'src/app/core/types/null-safe.type';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
})
export class TableComponent implements OnInit, OnChanges {
  @Input({ required: true }) data: NullSafe<Object[]> = []; // Array de objetos a serem exibidos na tabela
  @Input() counterColumn: boolean = true; // Exibe a coluna Nº com a contagem das linhas na tabela.
  @Input() maxShow: number = 0; // Limite de linhas a serem exibidas na tabela. Se = 0, todos os dados serão exibidos.
  @Input() hideColumns: string[] = []; // Esconde colunas específicas
  @Input() objectColumns: { [columnName: string]: string }[] = []; // Mapeia colunas que contêm objetos para o caminho do valor a ser exibido (usar nome original da coluna)
  @Input() replaceColumns: { [name: string]: string } = {}; // Substitui o nome das colunas
  @Input() cellTooltipColumns: Record<string, string> = {}; // Mapeia a coluna visível para o campo da linha que contém seu tooltip
  @Input() actionButtons: ActionButton[] = [];
  @Input() iconColumn: string; // Nome da coluna que exibe os ícones
  @Input() showPaginator: boolean = true; // Controla se o paginador deve ser exibido (obrigatório)
  @Input({ alias: 'pageSize' }) initialPageSize: 10 | 15 | 25 | 50 | 75 = 10; // Tamanho inicial da página do paginador
  @Input() showSearch: boolean = true; // Controla se o campo de busca deve ser exibido
  @Input() searchPlaceholder: string = 'Pesquisar'; // Placeholder do campo de busca
  @Input() rowClassProperty?: string; // Nome da propriedade que contém a classe CSS da linha
  @Input() clickableRows: boolean = true; // Linha com ação Detalhar elegível executa essa ação ao ser clicada; false desativa

  @ViewChild(MatPaginator) paginator: MatPaginator;

  protected iconRows: string[]; // Linhas com ícones

  // Propriedades para controle de paginação
  pageSize: number;
  currentPage: number = 0;
  pageSizeOptions: number[] = [10, 15, 25, 50, 75];

  // Propriedades para controle de busca
  searchTerm: string = '';
  filteredData: any[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Inicializa o pageSize com o valor do input
    this.pageSize = this.initialPageSize;
    this.validateSearchAndPaginator();
    this.validateActionButtons();

    this.actionButtons?.forEach(button => {
      if (button.callBack && button.routerLink) {
        throw new Error('callBack e routerLink não podem ser utilizados simultaneamente!');
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando os dados mudam, inicializa os dados filtrados e aplica o filtro
    if (changes['data'] && this.data) {
      this.filteredData = [...this.data];
      this.applyFilter();
      this.validateSearchAndPaginator();
      this.validateActionButtons();
    }
  }

  private validateActionButtons(): void {
    const typesParamsLink = ['number', 'string', 'boolean'];
    this.actionButtons = this.actionButtons.filter(button => button);

    this.actionButtons.forEach(button => {
      if (button.callBack && button.routerLink) {
        throw new Error('callBack e routerLink não podem ser utilizados simultaneamente!');
      }

      if (button.routerLink && button.params) {
        button.params.forEach(param => {
          if (typeof param === 'string') {
            return; // String é sempre válida
          }

          if (typeof param === 'object' && param !== null) {
            // Valida cada propriedade do objeto
            Object.keys(param).forEach(key => {
              let paramType = typeof param[key];
              if (param[key] == null) return;
              if (!typesParamsLink.includes(paramType)) {
                throw new Error(`Tipo de parâmetro '${key}' não é suportado para um routerLink. Os tipos suportados são: ${typesParamsLink.join(', ')}.`);
              }
            });
          }
        });
      }
    });
  }

  private validateSearchAndPaginator(): void {
    // Desativa o paginador se o total de itens for menor ou igual ao pageSize
    const length = this.data?.length ?? 0;
    this.showPaginator = !(length <= this.pageSize);
    this.showSearch = !(length <= this.pageSize);
  }

  get columns(): { keyName: string; displayName: string }[] {
    // Verifica se há dados disponíveis
    if (!this.data?.length) return [];

    // Obtém as chaves do primeiro objeto nos dados
    const filteredColumns = Object.keys(this.data[0])
      .filter(key => !this.hideColumns.includes(key) && key !== this.iconColumn) // Remove a coluna de ícone e colunas escondidas
      .map(key => {
        return { keyName: key, displayName: this.replaceColumns[key] || key }; // Substitui o nome das colunas, se necessário
      });

    // Salva os ícones das linhas (sempre com base nos dados originais)
    if (this.iconColumn) {
      this.iconRows = this.data.map(row => row[this.iconColumn]);
    }

    return filteredColumns;
  }

  get dataShow(): any[] {
    const dataToUse = this.showSearch ? this.filteredData : this.data;

    if (this.showPaginator) {
      // Se o paginador está ativo, aplicamos a paginação
      const startIndex = this.currentPage * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return dataToUse.slice(startIndex, endIndex);
    }

    // Se o paginador não está sendo exibido, aplicamos o maxShow tradicional
    return this.maxShow > 0 && this.maxShow < dataToUse?.length ? dataToUse.slice(0, this.maxShow) : dataToUse;
  }

  get totalItems(): number {
    if (this.showSearch) {
      return this.filteredData?.length || 0;
    }
    return this.data?.length || 0;
  }

  // Método para lidar com mudanças de página
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  // Método para aplicar filtro de busca
  onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.currentPage = 0; // Volta para primeira página ao pesquisar
    this.applyFilter();
  }

  // Método que aplica o filtro nos dados
  private applyFilter(): void {
    // Verifica se this.data é um array válido
    if (!this.data || !Array.isArray(this.data)) {
      this.filteredData = [];
      return;
    }

    // Se não há busca ativa ou termo de busca, mostra todos os dados
    if (!this.showSearch || !this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredData = [...this.data];
      return;
    }

    // Verifica se há dados disponíveis
    if (!this.data?.length) {
      this.filteredData = [];
      return;
    }

    // Obtém as colunas visíveis para pesquisar (sem usar o getter columns para evitar loop)
    const searchableColumns = Object.keys(this.data[0]).filter(key => !this.hideColumns.includes(key) && key !== this.iconColumn);

    this.filteredData = this.data.filter(row => {
      return searchableColumns.some(column => {
        // Usa getCellValue para considerar objectColumns na busca
        const value = this.getCellValue(row, column);
        if (value == null) return false;

        // Converte o valor para string e faz a busca case-insensitive
        return String(value).toLowerCase().includes(this.searchTerm);
      });
    });
  }

  // Método para obter o índice real da linha considerando a paginação
  getRealIndex(displayIndex: number): number {
    if (this.showPaginator) {
      return this.currentPage * this.pageSize + displayIndex + 1;
    }
    return displayIndex + 1;
  }

  // Método para obter o ícone da linha considerando a paginação e busca
  getIconForRow(displayIndex: number): string {
    if (!this.iconColumn) return '';

    if (this.showPaginator) {
      const realIndex = this.currentPage * this.pageSize + displayIndex;
      const dataToUse = this.showSearch ? this.filteredData : this.data;
      const actualRow = dataToUse[realIndex];

      if (actualRow && this.iconColumn in actualRow) {
        return actualRow[this.iconColumn];
      }
    }

    const dataToUse = this.showSearch ? this.filteredData : this.data;
    return dataToUse[displayIndex]?.[this.iconColumn] || '';
  }

  // Obtém o valor de uma célula, considerando objectColumns para objetos aninhados
  getCellValue(row: any, columnKeyName: string): any {
    const value = row[columnKeyName];

    // Verifica se existe configuração para esta coluna em objectColumns
    const objectColumnConfig = this.objectColumns?.find(config => Object.keys(config)[0] === columnKeyName);

    if (objectColumnConfig && value && typeof value === 'object') {
      const path = Object.values(objectColumnConfig)[0];
      // O caminho é absoluto desde a raiz do row (ex: 'ESPECIEDOCUMENTAL.TITULO')
      return this.resolveNestedPath(row, path);
    }

    return value;
  }

  getCellTooltip(row: any, columnKeyName: string): string {
    const path = this.cellTooltipColumns[columnKeyName];
    if (!path) return '';
    const value = this.resolveNestedPath(row, path);
    return value == null ? '' : String(value).trim();
  }

  // Resolve caminho de propriedades aninhadas (ex: 'TIPO.TITULO')
  private resolveNestedPath(obj: any, path: string): any {
    if (!obj || !path) return obj;

    const keys = path.split('.');
    let result = obj;

    for (const key of keys) {
      if (result == null) return null;
      result = result[key];
    }

    return result;
  }

  actionRouterLinkParams(button: ActionButton, row: any) {
    let routerLinkParams = {};
    if (button.routerLink) {
      button.params.forEach(param => {
        if (typeof param === 'string') {
          routerLinkParams[param.toLowerCase()] = typeof row[param] === 'string' ? Number(row[param]) || row[param] : row[param];
        } else if (typeof param === 'object') {
          Object.assign(routerLinkParams, param);
        }
      });
    }
    return routerLinkParams;
  }

  private actionCallBackParams(action: ActionButton, row: any) {
    let callBackParams = {};
    if (action.callBack) {
      action.params.forEach(param => {
        if (typeof param === 'string') {
          callBackParams[param] = typeof row[param] === 'string' ? Number(row[param]) || row[param] : row[param];
        } else if (typeof param === 'object') {
          Object.assign(callBackParams, param);
        }
      });
    }
    return callBackParams;
  }

  protected runCallBack(action: ActionButton, row: any) {
    if (typeof action.callBack !== 'function') return;
    const params = this.actionCallBackParams(action, row);
    action.callBack(...Object.values(params));
  }

  protected isRowClickable(row: any): boolean {
    return this.clickableRows && !!this.getDetalharAction(row);
  }

  protected onRowClick(row: any): void {
    if (!this.isRowClickable(row)) return;

    const action = this.getDetalharAction(row);
    if (!action) return;

    if (action.routerLink) {
      this.router.navigate([action.routerLink], {
        relativeTo: this.activatedRoute,
        queryParams: this.actionRouterLinkParams(action, row),
        state: action.state,
      });
      return;
    }

    if (action.callBack) {
      this.runCallBack(action, row);
    }
  }

  private getDetalharAction(row: any): ActionButton | undefined {
    return this.actionButtons?.find(action => {
      const isDetalhar = action.tooltip?.toLowerCase() === 'detalhar' || action.icon === 'mdi-open-in-new';
      if (!isDetalhar) return false;
      return !action.condition || action.condition(row);
    });
  }

  protected trackByIndex(index: number): number {
    return index;
  }
}

interface ActionButton {
  icon: string;
  tooltip?: string;
  routerLink?: string;
  callBack?: (...args: any[]) => void;
  params?: (string | { [name: string]: any })[];
  state?: { [name: string]: any };
  condition?: (row: any) => boolean;
}
